import React, { useEffect, useRef, useState } from "react";
import { memberFetch } from "../../lib/memberApi";
import { toast } from "sonner";
import { ShieldAlert } from "lucide-react";

/**
 * Full-screen blocking modal that requires every member to read and agree to
 * the release of liability before unlocking the member dashboard.
 *
 * Why blocking: liability waivers must be EXPLICIT, INFORMED, and recorded.
 * Showing it as a dismissable banner doesn't give Davon any legal cover.
 */
export default function WaiverModal({ memberName, onSigned }) {
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const scrollerRef = useRef(null);

  // Track when the user has scrolled to the bottom of the waiver text.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onScroll = () => {
      const reachedEnd = el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
      if (reachedEnd) setScrolledToEnd(true);
    };
    onScroll(); // in case waiver fits on screen
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const sign = async () => {
    if (!agreed || !scrolledToEnd) return;
    setSubmitting(true);
    try {
      await memberFetch("/member/waiver-sign", { method: "POST" });
      toast.success("Waiver signed. Welcome.");
      onSigned();
    } catch (e) {
      toast.error(e.message || "Could not record signature — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const canSign = scrolledToEnd && agreed && !submitting;

  return (
    <div
      className="fixed inset-0 z-[80] grid place-items-center bg-black/90 px-3 py-6"
      data-testid="waiver-modal"
    >
      <div className="w-full max-w-2xl border border-[#E5B43A]/40 bg-[#0F0F0F] flex flex-col max-h-[92vh]">
        <div className="px-6 py-5 border-b border-zinc-800 flex items-start gap-3">
          <ShieldAlert size={22} className="text-[#E5B43A] shrink-0 mt-0.5" />
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#E5B43A] mb-1">
              Required Before Access
            </div>
            <h2 className="font-display text-2xl sm:text-3xl">
              Release of Liability Waiver
            </h2>
            <p className="text-xs text-zinc-500 mt-2">
              Hey {memberName?.split(" ")[0] || "athlete"} — please read this carefully and sign at the bottom to unlock your workouts and meal plans.
            </p>
          </div>
        </div>

        <div
          ref={scrollerRef}
          className="flex-1 overflow-y-auto px-6 py-5 text-sm text-zinc-300 leading-relaxed space-y-4"
          data-testid="waiver-scroll"
        >
          <p>
            By signing below, I, the undersigned member of <b>Gain Time Fitness LLC</b>
            {" "}(&quot;Gain Time Fitness&quot;), acknowledge that participation in fitness training,
            strength and conditioning workouts, conditioning and cardio sessions, recovery
            and mobility programs, and meal/nutrition planning provided directly by
            Gain Time Fitness or through its online platform involves inherent physical
            risks, including but not limited to: muscular strain, sprain, fracture, joint
            injury, heart attack, dehydration, illness, and in extreme cases, death.
          </p>

          <p>
            <b>I represent that I am medically cleared</b> to participate in vigorous
            physical activity. If I have any current or past condition (heart, blood
            pressure, asthma, pregnancy, recent surgery, diabetes, eating disorder,
            allergies, or any other condition) that could be affected by exercise or
            dietary change, I will consult my physician BEFORE starting any workout or
            meal plan offered through this platform.
          </p>

          <p>
            <b>The AI-generated workouts and meal plans are general guidance only.</b>
            {" "}They are not personalized medical advice. They do not replace consultation
            with a doctor, registered dietitian, or licensed medical professional. I
            agree to listen to my body, scale intensity as needed, stop immediately if
            I feel pain, lightheadedness, or any abnormal symptom, and seek medical
            attention when warranted.
          </p>

          <p>
            <b>I assume all risk</b> of injury, illness, property damage, or death
            arising from my participation in any program, workout, meal plan, or
            session — whether in-person, virtual, or self-administered using guidance
            from this platform. I voluntarily and knowingly release, waive, and
            discharge Gain Time Fitness LLC, its owner Davon, its coaches, employees,
            contractors, agents, and affiliates from any and all liability, claims,
            demands, actions, or causes of action whatsoever, whether known or unknown,
            arising out of or related to my participation.
          </p>

          <p>
            I agree to indemnify and hold Gain Time Fitness harmless from any loss,
            liability, damage, or cost — including reasonable attorneys&apos; fees — that
            Gain Time Fitness may incur as a result of my participation.
          </p>

          <p>
            I confirm that I am at least 18 years of age (or have parental/guardian
            consent if a minor) and that I am signing this waiver voluntarily, of my
            own free will, with full understanding of its meaning and legal effect
            under the laws of the State of Texas.
          </p>

          <p>
            This waiver is binding upon me, my heirs, executors, administrators, and
            assigns. If any portion of this waiver is held invalid, the remainder
            shall continue in full force and effect.
          </p>

          <p className="text-zinc-500 italic">
            Scroll to the end and check the box below to electronically sign. Your
            timestamped signature is recorded for our records.
          </p>
        </div>

        <div className="px-6 py-5 border-t border-zinc-800 space-y-4 bg-[#0A0A0A]">
          {!scrolledToEnd && (
            <div className="text-[11px] tracking-widest uppercase text-amber-400 text-center" data-testid="waiver-scroll-hint">
              ↑ Please scroll through the full waiver to continue
            </div>
          )}
          <label className={`flex items-start gap-3 cursor-pointer select-none ${!scrolledToEnd ? "opacity-40 cursor-not-allowed" : ""}`}>
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              disabled={!scrolledToEnd}
              data-testid="waiver-agree-checkbox"
              className="mt-1 w-4 h-4 accent-[#E5B43A] shrink-0"
            />
            <span className="text-xs text-zinc-300 leading-relaxed">
              I have read, understood, and voluntarily agree to the Release of Liability
              Waiver above. I am signing electronically as my legal signature.
            </span>
          </label>

          <button
            onClick={sign}
            disabled={!canSign}
            data-testid="waiver-sign-button"
            className="w-full bg-[#E5B43A] text-black font-bold uppercase tracking-[0.2em] py-3.5 text-xs hover:bg-white hover:text-[#0A0A0A] transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-[#E5B43A] disabled:hover:text-white"
          >
            {submitting ? "Recording signature..." : "Sign & Continue"}
          </button>
        </div>
      </div>
    </div>
  );
}
